// src/pages/Specialties.jsx
import { useEffect, useMemo, useState } from 'react';
import PageBanner from '../components/ui/PageBanner';
import doctors from '../data/doctors.import.json';
import { toSpecialtyCategory } from '../utils/specialty';
import fallbackSpecialtyImg from '../assets/header-1.jpeg';
import './Specialties.css';

const SPECIALTY_IMAGES = {
  'ALLERGIST AND IMMUNOLOGIST': '/specialties/Allergist.jfif',
  AUDIOMETRY: '/specialties/audiometry.jfif',
  Cardiology: '/specialties/cardiology.jpg',
  CHIROPRACTOR: '/specialties/CHIROPRACTOR.jfif',
  'CONSULTANT DIABETOLOGIST': '/specialties/daibatologist.jfif',
  Dentistry: '/specialties/dentistry.jfif',
  Dermatology: '/specialties/dermatology.jfif',
  DIBE: '/specialties/dibe.png',
  'Diet & Nutrition': '/specialties/diet & nutrition.jfif',
  ENDOCRINOLOGIST: '/specialties/ENDOCRINOLOGIST.jfif',
  ENT: '/specialties/ENT.jfif',
  Gastroenterology: '/specialties/Gastroenterology.jfif',
  'General Surgery': '/specialties/General Surgery.jfif',
  Gynecology: '/specialties/Gynecology.jfif',
  'Internal Medicine': '/specialties/daibatologist.jfif',
  NEONATOLOGIST: '/specialties/NEONATOLOGIST.jfif',
  'Neurology / Neurosurgery': '/specialties/Neurology.jfif',
  Oncology: '/specialties/Oncology.jfif',
  Orthopedics: '/specialties/Orthopedics.jfif',
  PATHOLOGIST: '/specialties/PATHOLOGIST.jfif',
  Pediatrics: '/specialties/Pediatrics.jfif',
  PHYCOLOGIST: '/specialties/PHYCOLOGIST.jfif',
  'Plastic Surgery': '/specialties/Plastic Surgery.jfif',
  Psychiatry: '/specialties/Psychiatry.jfif',
  'Psychology / Speech': '/specialties/Psychology  Speech.jfif',
  Pulmonology: '/specialties/Pulmonology.jfif',
  PUMONOLIGIST: '/specialties/PUMONOLIGIST.jfif',
  Radiology: '/specialties/Radiology.jfif',
  RHEUMATOLOGIST: '/specialties/RHEUMATOLOGIST.jfif',
  'UROGOLIST AND INFERTILITY': '/specialties/UROGOLIST AND INFERTILITY.jfif',
  Urology: '/specialties/Urology.jfif',
};

function getSpecialtyImage(specialty) {
  return encodeURI(SPECIALTY_IMAGES[specialty] || fallbackSpecialtyImg);
}

function handleSpecialtyImageError(event) {
  event.currentTarget.onerror = null;
  event.currentTarget.src = fallbackSpecialtyImg;
}

export default function Specialties() {
  const [activeCategory, setActiveCategory] = useState('');

  const specialties = useMemo(() => {
    const set = new Set(doctors.map(d => toSpecialtyCategory(d.spec)).filter(Boolean));
    return [...set].sort((a, b) => a.localeCompare(b));
  }, []);

  const filteredDoctors = useMemo(() => {
    if (!activeCategory) return [];
    return doctors.filter(d => toSpecialtyCategory(d.spec) === activeCategory);
  }, [activeCategory]);

  useEffect(() => {
    const readHash = () => {
      const raw = (window.location.hash || '').replace('#', '').trim();
      if (!raw || !raw.startsWith('category=')) return;

      const cat = decodeURIComponent(raw.replace('category=', ''));
      setActiveCategory(cat);

      requestAnimationFrame(() => {
        const el = document.getElementById('spec-doctors');
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    };

    readHash();
    window.addEventListener('hashchange', readHash);
    return () => window.removeEventListener('hashchange', readHash);
  }, []);

  return (
    <>
      <PageBanner title="Specialties" crumbs={['Home', 'Specialties']} />

      <section className="section">
        <div className="section-inner">
          <div className="spec__header">
            <p className="section-label">Department List</p>
            <h3 className="section-title">Our Medical Specialties</h3>
            <p className="section-text">
              Explore our clinical specialties and find the right doctor for your needs. Select a
              specialty to view available consultants and OPD timings.
            </p>
          </div>

          <div className="spec__grid">
            {specialties.map(spec => (
              <button
                key={spec}
                type="button"
                className={`spec__card card ${activeCategory === spec ? 'spec__card--active' : ''}`}
                onClick={() => {
                  setActiveCategory(spec);
                  window.location.hash = `category=${encodeURIComponent(spec)}`;
                }}
              >
                <span className="spec__image">
                  <img
                    src={getSpecialtyImage(spec)}
                    alt={spec}
                    loading="lazy"
                    onError={handleSpecialtyImageError}
                  />
                </span>
                <span className="spec__name">{spec}</span>
              </button>
            ))}
          </div>

          {activeCategory ? (
            <div className="spec__doctors" id="spec-doctors">
              <div className="spec__doctors-head">
                <p className="section-label">Specialty Doctors</p>
                <h3 className="section-title spec__doctors-title">{activeCategory}</h3>
              </div>

              <div className="spec__doctors-grid">
                {filteredDoctors.map(d => (
                  <div key={`${d.id}-${d.name}`} className="spec__doctor-card card">
                    <div className="spec__doctor-top">
                      <h4 className="spec__doctor-name">{d.name}</h4>
                      <p className="spec__doctor-branch">{d.branch || '—'}</p>
                    </div>
                    <p className="spec__doctor-meta">
                      <strong>Department:</strong> {d.spec || '—'}
                    </p>
                    <p className="spec__doctor-meta">
                      <strong>OPD Days:</strong> {d.opdDays || '—'}
                    </p>
                    <p className="spec__doctor-meta">
                      <strong>Time:</strong> {d.time || '—'}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          ) : null}
        </div>
      </section>
    </>
  );
}
